import {computed, ref, reactive, getCurrentInstance, watch} from 'vue'
import store from "../../store"
import helpers from "@/lib/helpers";
import routeLeave from "@/router/route-leave";

export function useForm(fields, addUrl, record, onnew, onsave, opts) {
    opts = opts || {}
    const form = reactive(Object.fromEntries(fields.map(key => [key, null])))
    const state = ref({})
    const orig = reactive(Object.fromEntries(fields.map(key => [key, null])))
    const dialog = getCurrentInstance().proxy.$dialog
    const modify = record ? record : computed(() => store.state.record.active)

    const init = () => {
        state.value = {}
        fields.forEach(p => {
            if (modify.value)
                form[p] = modify.value[p]
            else if (opts && opts.defaults && opts.defaults[p] !== undefined)
                form[p] = opts.defaults[p]
            else
                form[p] = null

            orig[p] = modify.value ? modify.value[p] : null
        })
    }

    watch(modify, () => {
        init()
    }, {immediate: true})

    const formDialog = async (modal, params, perm) => {
        if ((!perm || helpers.can(perm)) && changed.value.length) {
            helpers.alert('Save or clear changes first')
            return;
        }

        await dialog.showAndWait(modal, params || {url: modify.value._links.self_url.href})
        !record && store.dispatch('record_load')
    }

    const formLeave = async (to, from, next) => {
        if (changed.value.length > 0) {
            if (!await helpers.confirm('Leave without saving?')) {
                next(false)
                return
            }
        }

        routeLeave(to, from, next)
    }

    // ---------- Type-tolerant equality ----------
    const isNullishOrEmpty = (v) => v === null || v === undefined || v === ''
    const parseNumeric = (v) => {
        if (typeof v === 'number') return Number.isFinite(v) ? v : null
        if (typeof v === 'string') {
            const s = v.trim()
            if (s === '') return null
            const n = Number(s)
            return Number.isFinite(n) ? n : null
        }
        return null
    }
    const parseBoolean = (v) => {
        if (typeof v === 'boolean') return v
        if (typeof v === 'string') {
            const s = v.trim().toLowerCase()
            if (s === 'true') return true
            if (s === 'false') return false
        }
        return null
    }
    const equivalent = (a, b) => {
        // '' ≡ null/undefined
        if (isNullishOrEmpty(a) && isNullishOrEmpty(b)) return true

        // numeric strings ≡ numbers
        const na = parseNumeric(a)
        const nb = parseNumeric(b)
        if (na !== null && nb !== null) return na === nb

        // "true"/"false" ≡ booleans
        const ba = parseBoolean(a)
        const bb = parseBoolean(b)
        if (ba !== null && bb !== null) return ba === bb

        // lists and objects (a JSON column) by content, not by instance
        if (Array.isArray(a) && Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b)
        if (a && b && typeof a === 'object' && typeof b === 'object' && !(a instanceof Date) && !(b instanceof Date))
            return JSON.stringify(a) === JSON.stringify(b)

        // strict for everything else
        return a === b
    }

    const getObjectDiff = (obj1, obj2) => {
        const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)])
        return [...keys].filter(k => !equivalent(obj1[k], obj2[k]))
    }

    const extractValues = (obj, keysArray) => {
        return keysArray.reduce((result, key) => {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                result[key] = obj[key];
            }
            return result;
        }, {});
    }

    const changed = computed(() => getObjectDiff(form, orig))

    const save = async () => {
        let result
        const force = Array.isArray(opts && opts.force) ? opts.force : false
        try {
            if (modify.value) {
                if (!force && changed.value.length === 0) {
                    helpers.toast('No changes to save', true)
                    return
                }

                // merge + dedupe changed keys with force keys
                let c = [...new Set([...(changed.value || []), ...((force || []))])]

                result = await helpers.asyncAction(modify.value._links.self_url.href, 'PUT', {
                        data: {
                            _orig: extractValues(orig, c),
                            ...extractValues(form, c),
                            ...((opts && opts.additional) || {})
                        }
                    }
                )

                if (record)
                    modify.value = result
                else
                    await store.commit('record_start', {type: store.state.record.type, record: result})

                onsave && onsave(record)

                state.value = {}
                !opts.notoast && helpers.toast("Saved " + (modify.value.gli || ''))
            } else {
                result = await helpers.asyncAction(addUrl, 'POST', {
                    data: {
                        ...form,
                        ...((opts && opts.additional) || {})
                    }
                })

                if (record)
                    modify.value = result
                else
                    await store.commit('record_start', {type: store.state.record.type, record: result})

                onnew && await onnew(result);

                state.value = {}
                !opts.notoast && helpers.toast("Added " + (result.gli || ''))
            }
        } catch (e) {
            if (e.name === 'RemoteError' && e.type === 'ValidationException')
                state.value = e.response.fields

            throw (e)
        }

        return result
    }

    return {form, orig, state, init, changed, save, modify, formDialog, formLeave}
}
